import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { confirmAlert } from 'react-confirm-alert';
import { getMallList } from 'app/api/mall';
import { getStoreListOnMallID, uplaodMedia } from 'app/api/store';
import { getDefaultTierByMallId } from 'app/api/tier';
import { getVoucherTermsAndConditionsList } from 'app/api/voucher';
import {
  createPackage,
  getPackageDetail,
  updatePackage,
} from 'app/api/package';
import {
  validate,
  packageValidationRules,
  packageVoucherValidationRules,
} from 'app/constants/validation-constants';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

// Sentinel for the "All tiers" option. Submitted as tierIds = [] (the DTO's
// "all tiers"). Unlike the voucher form there is deliberately NO Accrual
// option: the server refuses the Accrual sentinel for packages (REQ-09).
export const ALL_TIERS = 'ALL';

// Error key for the "pick a store" message of one voucher entry.
export const voucherStoreErrorKey = (key) => `voucherStore:${key}`;

const EMPTY_PANEL_VALUES = {
  storeId: '',
  voucherTitle: '',
  description: '',
  promotionalImage: '',
  loyaltyPoints: '',
  startDate: '',
  expiryDate: '',
};

// Numeric fields are held as STRINGS and converted once at submit: parseInt('')
// is NaN, and NaN serialises to null, which the server DTO rejects with a
// message the admin never sees.
const INITIAL_VALUES = {
  mallId: '',
  title: '',
  description: '',
  promotionalImage: '',
  tierIds: [],
  packageCount: '',
  usageLimit: '',
  frequency: '',
  sortOrder: '',
  termsOfUsage: [],
  howToRedeem: '',
  visible: true,
  status: true,
};

const blankIfNull = (v) => (v === null || v === undefined ? '' : String(v));

const useCreatePackage = () => {
  const navigate = useNavigate();
  // The package edit route carries the id in its path, so a refresh keeps it
  // (the voucher form reads location.state.id, which a refresh loses).
  const params = useParams();
  const packageId = params?.id ? Number(params.id) : null;
  const isEdit = Boolean(packageId);

  const [tierList, setTierList] = useState([]);
  const [mallList, setMallList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [voucherTermsConditions, setVoucherTermsConditions] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setErrors] = useState({});
  const [values, setValues] = useState(INITIAL_VALUES);
  const [everBought, setEverBought] = useState(false);

  // Draft voucher list. `id` present = already on the server; absent = draft.
  const [vouchers, setVouchers] = useState([]);
  const [removeVoucherIds, setRemoveVoucherIds] = useState([]);
  const vouchersRef = useRef([]);
  vouchersRef.current = vouchers;
  const keyCounter = useRef(0);

  // Side panel.
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [panelValues, setPanelValues] = useState(EMPTY_PANEL_VALUES);
  const [panelError, setPanelErrors] = useState({});

  const nextKey = () => {
    keyCounter.current += 1;
    return `draft-${keyCounter.current}`;
  };

  const showError = (response) => {
    Toast.error(
      response?.response?.message
        ? response?.response?.message
        : Message.Error.COMMON_MESSAGE
    );
  };

  // ---------------------------------------------------------------- loaders
  async function getMallListforDropdown() {
    setPageLoading(true);
    const response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      setMallList(
        (response?.data?.malls || []).map((item) => ({
          ...item,
          value: item.id,
          label: item.name,
        }))
      );
    } else {
      showError(response);
    }
  }

  // Returns the loaded list so edit hydration can resolve store names without
  // waiting for a re-render.
  async function getStoreListDropdown(value) {
    setPageLoading(true);
    const response = await getStoreListOnMallID(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      const list = (response?.data || []).map((item) => ({
        ...item,
        value: item.id,
        label: item.storeName,
      }));
      setStoreList(list);
      return list;
    }
    showError(response);
    return [];
  }

  async function getTierListDropdown(value) {
    setPageLoading(true);
    const response = await getDefaultTierByMallId(value);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      setTierList(
        (response?.data || []).map((item) => ({
          ...item,
          value: item.id,
          label: item.name,
        }))
      );
    } else {
      showError(response);
    }
  }

  async function getVoucherTermsAndConditionsDropdown() {
    setPageLoading(true);
    const response = await getVoucherTermsAndConditionsList();
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      setVoucherTermsConditions(
        (response?.data || []).map((item) => ({
          ...item,
          value: item.label,
          name: item.label,
        }))
      );
    } else {
      showError(response);
    }
  }

  // Edit hydration. Date fields stay the YYYY-MM-DD strings the server
  // returned: they are calendar labels and any date conversion shifts the day.
  async function loadPackageData(id) {
    setPageLoading(true);
    const response = await getPackageDetail(id);
    setPageLoading(false);
    if (response?.responseCode !== 200) {
      showError(response);
      return;
    }
    const data = response?.data || {};
    const rawTierIds = Array.isArray(data.tierIds)
      ? data.tierIds.filter((t) => t !== null && t !== undefined)
      : [];
    setValues({
      mallId: data.mallId ?? '',
      title: blankIfNull(data.title),
      description: blankIfNull(data.description),
      promotionalImage: blankIfNull(data.promotionalImage),
      // An empty stored array is "All tiers": hydrate the sentinel so it
      // renders a chip and passes the mandatory tier validation on re-save.
      tierIds: rawTierIds.length > 0 ? rawTierIds.map(String) : [ALL_TIERS],
      packageCount: blankIfNull(data.packageCount),
      usageLimit: blankIfNull(data.usageLimit),
      frequency: blankIfNull(data.frequency),
      sortOrder: blankIfNull(data.sortOrder),
      termsOfUsage: Array.isArray(data.termsOfUsage) ? data.termsOfUsage : [],
      howToRedeem: blankIfNull(data.howToRedeem),
      visible: data.visible !== false,
      status: data.status !== false,
    });
    setEverBought(Boolean(data.everBought));

    const stores = await getStoreListDropdown(data.mallId);
    await getTierListDropdown(data.mallId);
    setVouchers(
      (data.vouchers || []).map((v) => {
        const store = stores.find((s) => String(s.id) === String(v.storeId));
        return {
          key: `saved-${v.id}`,
          id: v.id,
          storeId: v.storeId ?? '',
          // The detail response carries no store name per member.
          storeName: store?.storeName || '',
          voucherTitle: blankIfNull(v.voucherTitle),
          description: blankIfNull(v.description),
          promotionalImage: blankIfNull(v.promotionalImage),
          loyaltyPoints: blankIfNull(v.loyaltyPoints),
          startDate: blankIfNull(v.startDate),
          expiryDate: blankIfNull(v.expiryDate),
          everReserved: Boolean(v.everReserved),
          needsStore: false,
        };
      })
    );
  }

  useEffect(() => {
    getMallListforDropdown();
    getVoucherTermsAndConditionsDropdown();
    if (packageId) {
      loadPackageData(packageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------- package fields
  function clearError(name) {
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  }

  function handleChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;
      setValues((prev) => ({ ...prev, [name]: value }));
      clearError(name);
    }
  }

  function setChange(name, val) {
    setValues((prev) => ({ ...prev, [name]: val }));
    clearError(name);
  }

  // Dates arrive as the raw YYYY-MM-DD input value and stay that way.
  function handleTime(value, name) {
    setChange(name, value);
  }

  function handleTierChange(e) {
    if (!(e && e.target)) {
      return;
    }
    const incoming = e.target.value || [];
    const prev = values.tierIds || [];
    const allJustAdded =
      incoming.includes(ALL_TIERS) && !prev.includes(ALL_TIERS);
    let next;
    if (allJustAdded) {
      next = [ALL_TIERS];
    } else if (incoming.includes(ALL_TIERS) && incoming.length > 1) {
      next = incoming.filter((v) => v !== ALL_TIERS);
    } else {
      next = incoming;
    }
    setValues((p) => ({ ...p, tierIds: next }));
    clearError('tierIds');
  }

  async function uploadFile(file) {
    const response = await uplaodMedia(file);
    if (response?.responseCode === 200) {
      Toast.success('Image upload Successfull');
      return response?.data?.url;
    }
    showError(response);
    return undefined;
  }

  // A null file is a removal, as in the voucher form.
  async function uploadMediaHandler(file, name) {
    if (!file) {
      setChange(name, null);
      return;
    }
    const url = await uploadFile(file);
    if (url !== undefined) {
      setChange(name, url);
    }
  }

  // ----------------------------------------------------------- mall change
  function applyMallChange(value) {
    setStoreList([]);
    setTierList([]);
    getStoreListDropdown(value);
    getTierListDropdown(value);
    setValues((prev) => ({ ...prev, mallId: value, tierIds: [] }));
    // Every voucher keeps its title, image, description and points; only the
    // store goes, and each entry is flagged so Submit stays blocked until
    // every one has a store again. The server's cross-mall refusal is the
    // backstop; this is the usability guard in front of it.
    setVouchers((prev) =>
      prev.map((v) => ({ ...v, storeId: '', storeName: '', needsStore: true }))
    );
    setPanelValues((prev) => ({ ...prev, storeId: '' }));
    setErrors((prev) => {
      const next = { ...prev, mallId: '', tierIds: '' };
      Object.keys(next).forEach((k) => {
        if (k.startsWith('voucherStore:')) {
          next[k] = '';
        }
      });
      return next;
    });
  }

  function handleMallChange(e) {
    if (!(e && e.target)) {
      return;
    }
    const { value } = e.target;
    if (String(value) === String(values.mallId)) {
      return;
    }
    if (vouchers.length === 0) {
      applyMallChange(value);
      return;
    }
    confirmAlert({
      title: '',
      message: Message.Package.MALL_CHANGE_WARNING,
      buttons: [
        {
          label: 'Yes',
          onClick: () => applyMallChange(value),
        },
        {
          label: 'No',
        },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  }

  // ----------------------------------------------------------- voucher list
  function addVoucher(entry) {
    const newEntry = {
      storeId: '',
      storeName: '',
      voucherTitle: '',
      description: '',
      promotionalImage: '',
      loyaltyPoints: '',
      startDate: '',
      expiryDate: '',
      ...entry,
      key: nextKey(),
      everReserved: false,
      needsStore: false,
    };
    delete newEntry.id;
    setVouchers((prev) => [...prev, newEntry]);
    clearError('vouchers');
  }

  // Replaces in place by key, keeping position, `id` and `everReserved`.
  function editVoucher(key, entry) {
    setVouchers((prev) =>
      prev.map((v) =>
        v.key === key
          ? {
              ...v,
              ...entry,
              key: v.key,
              id: v.id,
              everReserved: v.everReserved,
              needsStore: !(entry?.storeId ?? v.storeId),
            }
          : v
      )
    );
    clearError(voucherStoreErrorKey(key));
  }

  function removeVoucher(key) {
    const entry = vouchersRef.current.find((v) => v.key === key);
    if (!entry) {
      return;
    }
    // D-03: "bought" includes cancelled purchases; the server refuses this
    // removal, and the control is disabled up front.
    if (entry.everReserved) {
      return;
    }
    setVouchers((prev) => prev.filter((v) => v.key !== key));
    if (entry.id !== undefined && entry.id !== null) {
      setRemoveVoucherIds((prev) => [...prev, entry.id]);
    }
  }

  // REQ-05: display-only; the server recomputes the authoritative figure.
  const packagePoints = vouchers.reduce(
    (sum, v) => sum + (parseInt(v.loyaltyPoints, 10) || 0),
    0
  );

  // ------------------------------------------------------------- side panel
  function openPanel(key = null) {
    const entry = key ? vouchers.find((v) => v.key === key) : null;
    setPanelErrors({});
    if (entry) {
      setSelectedKey(entry.key);
      setPanelValues({
        storeId: entry.storeId ?? '',
        voucherTitle: entry.voucherTitle,
        description: entry.description,
        promotionalImage: entry.promotionalImage,
        loyaltyPoints: entry.loyaltyPoints,
        startDate: entry.startDate || '',
        expiryDate: entry.expiryDate || '',
      });
    } else {
      setSelectedKey(null);
      setPanelValues(EMPTY_PANEL_VALUES);
    }
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setSelectedKey(null);
    setPanelValues(EMPTY_PANEL_VALUES);
    setPanelErrors({});
  }

  function setPanelChange(name, val) {
    setPanelValues((prev) => ({ ...prev, [name]: val }));
    setPanelErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  }

  function handlePanelChange(e) {
    if (e && e.target) {
      setPanelChange(e.target.name, e.target.value);
    }
  }

  async function uploadPanelImage(file) {
    if (!file) {
      setPanelChange('promotionalImage', null);
      return;
    }
    const url = await uploadFile(file);
    if (url !== undefined) {
      setPanelChange('promotionalImage', url);
    }
  }

  // Returns true when the voucher entered the list.
  function handlePanelSubmit(e) {
    e?.preventDefault?.();
    const errors = validate(
      { ...panelValues, voucherTitle: (panelValues.voucherTitle || '').trim() },
      packageVoucherValidationRules
    );
    setPanelErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }
    const store = storeList.find(
      (s) => String(s.id) === String(panelValues.storeId)
    );
    const entry = {
      storeId: panelValues.storeId,
      storeName: store?.storeName || '',
      voucherTitle: panelValues.voucherTitle.trim(),
      description: panelValues.description,
      promotionalImage: panelValues.promotionalImage,
      loyaltyPoints: String(panelValues.loyaltyPoints),
      startDate: panelValues.startDate,
      expiryDate: panelValues.expiryDate,
    };
    if (selectedKey) {
      editVoucher(selectedKey, entry);
    } else {
      addVoucher(entry);
    }
    closePanel();
    return true;
  }

  // ----------------------------------------------------------------- submit
  function buildPayload() {
    const isAllTiers = (values.tierIds || []).includes(ALL_TIERS);
    const payload = {
      mallId: parseInt(values.mallId, 10),
      title: values.title.trim(),
      description: values.description,
      // [] = "All tiers". Never a zero: that is the Accrual sentinel.
      tierIds: isAllTiers
        ? []
        : [...new Set((values.tierIds || []).map(Number))].filter(
            (n) => Number.isInteger(n) && n > 0
          ),
      packageCount: parseInt(values.packageCount, 10),
      usageLimit: parseInt(values.usageLimit, 10),
      // The one numeric the server wants as a string of digits.
      frequency: String(values.frequency),
      termsOfUsage: values.termsOfUsage,
      howToRedeem: values.howToRedeem,
      visible: Boolean(values.visible),
      status: Boolean(values.status),
      vouchers: vouchers.map((v) => {
        const member = {
          storeId: parseInt(v.storeId, 10),
          voucherTitle: v.voucherTitle,
          description: v.description,
          promotionalImage: v.promotionalImage || '',
          loyaltyPoints: parseInt(v.loyaltyPoints, 10),
          startDate: v.startDate,
          expiryDate: v.expiryDate,
        };
        if (isEdit && v.id !== undefined && v.id !== null) {
          member.id = v.id;
        }
        return member;
      }),
    };
    if (values.sortOrder !== '' && values.sortOrder !== null) {
      payload.sortOrder = parseInt(values.sortOrder, 10);
    }
    if (values.promotionalImage) {
      payload.promotionalImage = values.promotionalImage;
    } else if (isEdit) {
      // The server preserves an ABSENT optional field but clears one sent
      // empty, so an image the admin removed must be sent explicitly.
      payload.promotionalImage = '';
    }
    return payload;
  }

  async function callAddOrUpdateAPI() {
    setPageLoading(true);
    try {
      const payload = buildPayload();
      const response = isEdit
        ? await updatePackage({ ...payload, id: packageId, removeVoucherIds })
        : await createPackage(payload);
      if (response?.responseCode === 200) {
        Toast.success(
          isEdit
            ? Message.Package.UPDATED_SUCCESS
            : Message.Package.CREATED_SUCCESS
        );
        navigate(-1);
      } else {
        // Minimum-one, count-floor, removal-block and cross-mall refusals
        // arrive here as service messages.
        showError(response);
      }
    } catch (err) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    const validationErrors = validate(
      { ...values, title: (values.title || '').trim() },
      packageValidationRules
    );
    if (vouchers.length === 0) {
      validationErrors.vouchers = Message.Package.MIN_ONE_VOUCHER;
    }
    vouchers.forEach((v) => {
      if (v.needsStore || !v.storeId) {
        validationErrors[voucherStoreErrorKey(v.key)] =
          Message.Package.PICK_STORE;
      } else if (!v.startDate || !v.expiryDate) {
        validationErrors[voucherStoreErrorKey(v.key)] =
          Message.Package.PICK_DATES;
      }
    });
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      return callAddOrUpdateAPI();
    }
    return undefined;
  }

  const tierIdsOptions = useMemo(
    () => [
      { value: ALL_TIERS, name: 'All' },
      ...tierList.map((t) => ({
        ...t,
        value: String(t.value),
        name: t.name ?? t.label,
      })),
    ],
    [tierList]
  );

  return {
    // package fields
    values,
    error,
    setValues,
    handleChange,
    setChange,
    handleTime,
    handleTierChange,
    handleMallChange,
    uploadMediaHandler,
    handleSubmit,
    // lookups
    mallList,
    storeList,
    tierList,
    tierIdsOptions,
    voucherTermsConditions,
    // voucher list
    vouchers,
    removeVoucherIds,
    packagePoints,
    addVoucher,
    editVoucher,
    removeVoucher,
    // side panel
    panelOpen,
    panelMode: selectedKey ? 'save' : 'add',
    selectedKey,
    panelValues,
    panelError,
    openPanel,
    closePanel,
    handlePanelChange,
    setPanelChange,
    uploadPanelImage,
    handlePanelSubmit,
    // misc
    packageId,
    isEdit,
    everBought,
    pageLoading,
    navigate,
  };
};

export default useCreatePackage;
